import { useState, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import Stack from '@mui/material/Stack';
import { PageHeader } from '../../components/common/PageHeader';
import { EmptyState } from '../../components/common/EmptyState';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteIcon from '@mui/icons-material/Delete';
import MicIcon from '@mui/icons-material/Mic';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  useGetFirefliesStatus,
  useListImportedTranscripts,
  useListTranscripts,
  useCreateConversation,
  useCreateTranscript,
  useDeleteTranscript,
  useUploadAndCreateTranscript,
  getListTranscriptsQueryKey,
} from '../../api/generated/generated';
import type {
  ConversationResponse,
  ImportedTranscriptSummary,
  UserTranscriptDetail,
  UserTranscriptSummary,
} from '../../api/generated';
import { FirefliesTranscriptBrowser } from '../../components/transcripts/FirefliesTranscriptBrowser';

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

const SOURCE_LABELS: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' }> =
  {
    fireflies: { label: 'Fireflies', color: 'primary' },
    paste: { label: 'Pasted', color: 'default' },
    upload: { label: 'Uploaded', color: 'secondary' },
  };

function SourceChip({ source }: { source: string }) {
  const config = SOURCE_LABELS[source] ?? { label: source, color: 'default' as const };
  return <Chip label={config.label} size="small" color={config.color} variant="outlined" />;
}

export function TranscriptsPage() {
  const navigate = useNavigate();
  const { data: statusResp, isLoading: statusLoading } = useGetFirefliesStatus();
  const status = statusResp?.status === 200 ? statusResp.data : undefined;
  const isConnected = status?.status === 'connected';

  // Unified transcript list
  const { data: transcriptsResp, isLoading: transcriptsLoading } = useListTranscripts();
  const transcriptsData =
    transcriptsResp?.status === 200 ? transcriptsResp.data : undefined;
  const transcripts = transcriptsData?.transcripts ?? [];

  // Fireflies imported IDs (for the import browser)
  const { data: importedResp } = useListImportedTranscripts();
  const importedFirefliesIds = useMemo(() => {
    const imported = importedResp?.status === 200 ? importedResp.data : undefined;
    return new Set(
      (imported?.transcripts ?? []).map((t: ImportedTranscriptSummary) => t.fireflies_id),
    );
  }, [importedResp]);

  const queryClient = useQueryClient();
  const invalidateTranscripts = () =>
    queryClient.invalidateQueries({ queryKey: getListTranscriptsQueryKey() });

  const deleteMutation = useDeleteTranscript({ mutation: { onSuccess: invalidateTranscripts } });
  const createMutation = useCreateTranscript({ mutation: { onSuccess: invalidateTranscripts } });
  const uploadMutation = useUploadAndCreateTranscript({
    mutation: { onSuccess: invalidateTranscripts },
  });
  const createConversation = useCreateConversation();

  const [browserOpen, setBrowserOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTab, setCreateTab] = useState(0);
  const [pasteTitle, setPasteTitle] = useState('');
  const [pasteContent, setPasteContent] = useState('');
  // Id of the transcript currently being analyzed (row action or create-dialog).
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  // Id of a just-created transcript, used for the create-dialog "Analyze now" step.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const resetCreateDialog = () => {
    setCreateOpen(false);
    setCreateTab(0);
    setPasteTitle('');
    setPasteContent('');
    setCreatedId(null);
  };

  // Create a post-call analysis for a transcript and navigate to it. Reused by the
  // per-row Analyze action and the create-dialog "Analyze now" step. The streaming
  // analysis + "Analyzing your call…" indicator take over on the conversation view.
  const analyzeTranscript = async (transcriptId: string) => {
    setAnalyzingId(transcriptId);
    setActionError(null);
    try {
      const res = await createConversation.mutateAsync({
        data: { mode: 'post_call', transcript_id: transcriptId },
      });
      const conv = res.data as ConversationResponse;
      navigate(`/chat/${conv.id}`);
    } catch {
      setActionError('Could not start the analysis. Please try again.');
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync({ transcriptId: id });
    } catch {
      // Error handled by mutation
    }
  };

  const handlePasteCreate = async () => {
    try {
      const res = await createMutation.mutateAsync({
        data: { title: pasteTitle, content: pasteContent },
      });
      if (res.status === 201) {
        setCreatedId((res.data as UserTranscriptDetail).id);
      }
    } catch {
      // Error handled by mutation
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const res = await uploadMutation.mutateAsync({ data: { file } });
      if (res.status === 201) {
        setCreatedId((res.data as UserTranscriptDetail).id);
      }
    } catch {
      // Error handled by mutation
    }
    e.target.value = '';
  };

  if (statusLoading || transcriptsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Transcripts"
        subtitle="Collect your call transcripts here — paste text, upload a file, or import from Fireflies — then analyze them with the AI coach."
        actions={
          <>
            <Tooltip title="Paste text or upload a file">
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                Create
              </Button>
            </Tooltip>
            {isConnected && (
              <Tooltip title="Import transcripts from your connected Fireflies account">
                <Button
                  variant="contained"
                  startIcon={<CloudDownloadIcon />}
                  onClick={() => setBrowserOpen(true)}
                >
                  Import from Fireflies
                </Button>
              </Tooltip>
            )}
            {!isConnected && (
              <Tooltip title="Connect Fireflies to import your call recordings">
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={() => navigate('/settings')}
                >
                  Connect Fireflies
                </Button>
              </Tooltip>
            )}
          </>
        }
      />
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {transcripts.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <EmptyState
            icon={<RecordVoiceOverIcon />}
            title="No Transcripts Yet"
            description="This is where your call transcripts live. Add one by pasting text, uploading a file (PDF, DOCX, TXT, MD, CSV), or importing from Fireflies — then analyze it with the AI coach."
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                Create Transcript
              </Button>
            }
          />
        </Paper>
      ) : (
        <Paper>
          <List disablePadding>
            {transcripts.map((transcript: UserTranscriptSummary, index: number) => (
              <ListItem
                key={transcript.id}
                divider={index < transcripts.length - 1}
                secondaryAction={
                  <Stack direction="row" spacing={1} sx={{
                    alignItems: "center"
                  }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        analyzingId === transcript.id ? (
                          <CircularProgress size={14} />
                        ) : (
                          <AssessmentIcon />
                        )
                      }
                      onClick={() => analyzeTranscript(transcript.id)}
                      disabled={analyzingId !== null}
                    >
                      Analyze
                    </Button>
                    <Tooltip title="Delete transcript">
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(transcript.id)}
                        disabled={deleteMutation.isPending || analyzingId !== null}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} sx={{
                      alignItems: "center"
                    }}>
                      <Typography variant="body1">{transcript.title}</Typography>
                      <SourceChip source={transcript.source} />
                      {transcript.submitted_to_coach && (
                        <Chip label="Coached" size="small" color="success" variant="outlined" />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      {new Date(transcript.date).toLocaleDateString()}
                      {transcript.duration > 0 && ` \u00b7 ${formatDuration(transcript.duration)}`}
                      {transcript.preview && ` \u00b7 ${transcript.preview.slice(0, 80)}...`}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      {transcriptsData?.has_more && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="text" size="small">
            Load More
          </Button>
        </Box>
      )}
      {/* Create Transcript Dialog */}
      <Dialog open={createOpen} onClose={resetCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create Transcript</DialogTitle>
        {createdId ? (
          <>
            <DialogContent>
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Transcript created
                </Typography>
                <Typography sx={{
                  color: "text.secondary"
                }}>
                  Analyze it now with the AI coach, or come back to it later from this list.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={resetCreateDialog}>Done</Button>
              <Button
                variant="contained"
                startIcon={
                  analyzingId === createdId ? (
                    <CircularProgress size={16} />
                  ) : (
                    <AssessmentIcon />
                  )
                }
                onClick={() => analyzeTranscript(createdId)}
                disabled={analyzingId !== null}
              >
                Analyze now
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent>
              <Tabs value={createTab} onChange={(_, v) => setCreateTab(v)} sx={{ mb: 2 }}>
                <Tab icon={<ContentPasteIcon />} label="Paste" iconPosition="start" />
                <Tab icon={<UploadFileIcon />} label="Upload" iconPosition="start" />
              </Tabs>

              {createTab === 0 && (
                <Stack spacing={2}>
                  <TextField
                    label="Title"
                    value={pasteTitle}
                    onChange={(e) => setPasteTitle(e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Transcript Content"
                    value={pasteContent}
                    onChange={(e) => setPasteContent(e.target.value)}
                    fullWidth
                    multiline
                    minRows={8}
                    maxRows={20}
                    placeholder="Paste your call transcript here..."
                  />
                </Stack>
              )}

              {createTab === 1 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <MicIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography gutterBottom sx={{
                    color: "text.secondary"
                  }}>
                    Upload a PDF, DOCX, or text file
                  </Typography>
                  <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                    Choose File
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.docx,.txt,.md,.csv"
                      onChange={handleFileUpload}
                    />
                  </Button>
                  {uploadMutation.isPending && <CircularProgress size={24} sx={{ ml: 2 }} />}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={resetCreateDialog}>Cancel</Button>
              {createTab === 0 && (
                <Button
                  variant="contained"
                  onClick={handlePasteCreate}
                  disabled={!pasteTitle.trim() || !pasteContent.trim() || createMutation.isPending}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* Fireflies Import Browser */}
      <FirefliesTranscriptBrowser
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        importedFirefliesIds={importedFirefliesIds}
      />
    </Box>
  );
}
